import { type AccentColorTokens } from './theme-accent.constants';

function expandHex(value: string): string {
  const raw = value.replace('#', '').trim();
  if (raw.length !== 3) return raw;
  return raw.split('').map((char) => `${char}${char}`).join('');
}

export function normalizeHexColor(value: string): string | null {
  const expanded = expandHex(value);
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;
  return `#${expanded.toLowerCase()}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = normalizeHexColor(hex) ?? '#2563eb';
  const value = normalized.slice(1);
  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

export function hexToHslTriplet(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) return `0 0% ${Math.round(lightness * 100)}%`;

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;
  if (max === red) hue = ((green - blue) / delta) % 6;
  if (max === green) hue = (blue - red) / delta + 2;
  if (max === blue) hue = (red - green) / delta + 4;
  const normalizedHue = Math.round(hue * 60 < 0 ? hue * 60 + 360 : hue * 60);

  return `${normalizedHue} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

function getRelativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const channels = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

export function getCustomAccentTokens(hex: string): AccentColorTokens {
  const normalized = normalizeHexColor(hex) ?? '#2563eb';
  return {
    primary: hexToHslTriplet(normalized),
    primaryForeground: getRelativeLuminance(normalized) > 0.45 ? '222.2 47.4% 11.2%' : '210 40% 98%',
    ring: hexToHslTriplet(normalized),
  };
}
